import { c as _c } from "react/compiler-runtime";
import classnames from 'classnames';
import type { RefObject, JSX } from 'react';
import { useLayoutEffect } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useModalPortalContainer } from './context';
import { useOnClickOutside } from '../../hooks/click-outside';
import { useFocusTrapKeyHandler } from '../../hooks/use-focus-trap-keyhandler';
import { zIndices } from '../../theme';
export interface ModalProps {
  bottom?: string;
  children: React.ReactNode;
  hideOnClickOutside?: boolean;
  onClickOutside?: () => void;
  innerRef?: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  maxWidth?: string;
  minWidth?: string;
  onToggle: (isOpen: boolean) => unknown;
  overlay?: boolean;
  overlayBgColor?: 'default' | 'black' | 'white';
  overlayOpacity?: number;
  testId?: string;
  top?: string;
  width?: string;
  zIndex?: number;
  disableCloseOnEscape?: boolean;
  ariaLabel?: string;
  style?: React.CSSProperties;
}
export function Modal(props) {
  const $ = _c(40);
  const {
    bottom,
    children,
    hideOnClickOutside: t0,
    onClickOutside,
    disableCloseOnEscape: t1,
    innerRef,
    isOpen,
    maxWidth: t2,
    minWidth: t3,
    onToggle,
    overlay: t4,
    overlayBgColor: t5,
    overlayOpacity: t6,
    testId,
    top: t7,
    width: t8,
    zIndex,
    ariaLabel,
    style
  } = props;
  const hideOnClickOutside = t0 === undefined ? false : t0;
  const disableCloseOnEscape = t1 === undefined ? false : t1;
  const maxWidth = t2 === undefined ? "auto" : t2;
  const minWidth = t3 === undefined ? "auto" : t3;
  const overlay = t4 === undefined ? true : t4;
  const overlayBgColor = t5 === undefined ? "default" : t5;
  const overlayOpacity = t6 === undefined ? 0.57 : t6;
  const top = t7 === undefined ? 64 : t7;
  const width = t8 === undefined ? 600 : t8;
  const modalContainer = useModalPortalContainer();
  const modalRef = React.useRef(null);
  let t9;
  if ($[0] !== disableCloseOnEscape || $[1] !== isOpen || $[2] !== onToggle) {
    t9 = () => {
      if (!isOpen) {
        return _temp;
      }
      const handler = e => {
        if (e.key === "Escape" && !disableCloseOnEscape) {
          onToggle(false);
          e.stopImmediatePropagation();
        }
      };
      document.body.addEventListener("keydown", handler);
      return () => document.body.removeEventListener("keydown", handler);
    };
    $[0] = disableCloseOnEscape;
    $[1] = isOpen;
    $[2] = onToggle;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  let t10;
  if ($[4] !== onToggle) {
    t10 = [onToggle];
    $[4] = onToggle;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[6] !== isOpen) {
    t11 = {
      containerRef: modalRef,
      visible: isOpen
    };
    $[6] = isOpen;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  useFocusTrapKeyHandler(t11);
  let t12;
  let t13;
  if ($[8] !== isOpen) {
    t12 = () => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      }
      return _temp2;
    };
    t13 = [isOpen];
    $[8] = isOpen;
    $[9] = t12;
    $[10] = t13;
  } else {
    t12 = $[9];
    t13 = $[10];
  }
  useLayoutEffect(t12, t13);
  let t14;
  if ($[11] !== overlay) {
    t14 = classnames({
      overlay: true,
      show: overlay
    });
    $[11] = overlay;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const overlayClasses = t14;
  let t15;
  if ($[13] !== hideOnClickOutside || $[14] !== isOpen || $[15] !== onClickOutside || $[16] !== onToggle) {
    t15 = () => {
      if (!isOpen || !hideOnClickOutside) {
        return;
      }
      if (onClickOutside) {
        onClickOutside();
        return;
      }
      onToggle(false);
    };
    $[13] = hideOnClickOutside;
    $[14] = isOpen;
    $[15] = onClickOutside;
    $[16] = onToggle;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  useOnClickOutside(innerRef as RefObject<HTMLElement>, t15);
  if (!isOpen) {
    return null;
  }
  const t16 = bottom || "64px";
  let t17;
  if ($[18] !== maxWidth || $[19] !== minWidth || $[20] !== style || $[21] !== t16 || $[22] !== top || $[23] !== width) {
    t17 = {
      width,
      minWidth,
      maxWidth,
      marginTop: top,
      marginBottom: t16,
      ...style
    };
    $[18] = maxWidth;
    $[19] = minWidth;
    $[20] = style;
    $[21] = t16;
    $[22] = top;
    $[23] = width;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== ariaLabel || $[26] !== children || $[27] !== innerRef || $[28] !== t17) {
    t18 = <StyledModal ref={innerRef} className="ui-modal" role="dialog" aria-modal={true} aria-label={ariaLabel} style={t17}>{children}</StyledModal>;
    $[25] = ariaLabel;
    $[26] = children;
    $[27] = innerRef;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== overlayBgColor || $[31] !== overlayClasses || $[32] !== overlayOpacity || $[33] !== t18 || $[34] !== testId || $[35] !== zIndex) {
    t19 = <StyledOverlay overlayBgColor={overlayBgColor} overlayOpacity={overlayOpacity} className={overlayClasses} data-testid={testId} ref={modalRef} zIndex={zIndex} tabIndex={-1}>{t18}</StyledOverlay>;
    $[30] = overlayBgColor;
    $[31] = overlayClasses;
    $[32] = overlayOpacity;
    $[33] = t18;
    $[34] = testId;
    $[35] = zIndex;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = modalContainer?.current || document.body;
  let t21;
  if ($[37] !== t19 || $[38] !== t20) {
    t21 = createPortal(t19, t20);
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  return t21;
}
function _temp2() {
  document.body.style.overflow = "";
}
function _temp() {
  return null;
}
const overlayAnimation = (overlayOpacity: number | undefined, overlayBgColor: 'default' | 'white' | 'black' | undefined) => keyframes`
  from {
    background: ${getBackgroundColor(overlayBgColor, 0)};
  }
  to {
    background: ${getBackgroundColor(overlayBgColor, overlayOpacity ?? 0.5)};
  }
`;
const getBackgroundColor = (color: string | undefined, opacity: number) => {
  switch (color) {
    case 'white':
      return `rgba(255, 255, 255, ${opacity})`;
    case 'black':
      return `rgba(0, 0, 0, ${opacity})`;
    case 'default':
    default:
      return `rgba(31, 21, 41, ${opacity})`;
    // default fallback
  }
};
const modalAnimation = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;
const StyledOverlay = styled.div<{
  overlayOpacity?: number;
  overlayBgColor?: 'default' | 'black' | 'white';
  zIndex?: number;
}>`
  background: transparent;
  z-index: ${({
  zIndex
}) => zIndex ?? zIndices.overlay};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  &.show {
    animation: ${({
  overlayOpacity,
  overlayBgColor
}) => overlayAnimation(overlayOpacity, overlayBgColor)} 99ms ease-in;
    animation-fill-mode: both;
    background: ${({
  overlayOpacity,
  overlayBgColor
}) => {
  switch (overlayBgColor) {
    case 'white':
      return `rgba(255, 255, 255, ${overlayOpacity})`;
    case 'black':
      return `rgba(0, 0, 0, ${overlayOpacity})`;
    case 'default':
    default:
      return `rgba(31, 21, 41, ${overlayOpacity})`;
  }
}};
  }
`;
const StyledModal = styled.div`
  animation: ${modalAnimation} 300ms cubic-bezier(0.16, 0.68, 0.165, 1.3);
  position: relative;
  margin-left: auto;
  margin-right: auto;
  z-index: ${zIndices.modal};
  &:focus {
    outline: none;
  }
`;